import moment from "moment-mini";
import {cleanTextElement} from "./displayComponents";
import type {DataDiff} from "../types/utils";

type AnyObject = Record<string, any>;

type DataHelperObject = {
  getValueTextRow: (value: unknown, heading: string, permission?: unknown) => string;
  capitalize: (str: string) => string;
  processDate: (value: unknown) => string;
  formatDate: (value: unknown) => string;
  managedTypes: string[];
  getRandomInt: (min: number, max: number) => number;
  states: {australia: Array<{value: string; text: string}>};
  getOrderType: (key: string) => number;
  renewalTypes: string[];
  toUtc: (value: unknown) => string;
  countries: Array<{text: string; value: string}>;
  removeDashOnText: (value: string) => string;
  isObjEmpty: (obj: AnyObject) => boolean;
  isArrayEmpty: (arr: unknown[] | null | undefined) => boolean;
  arrayHasEmptyValue: (arr: unknown[] | null | undefined) => boolean;
  hasTimeVariables: (sqlQueryString: string) => boolean;
  basicArraysAreEqual: (array1: unknown[], array2: unknown[]) => boolean;
  basicObjectsAreEqual: (obj1: AnyObject, obj2: AnyObject, isEqual?: boolean) => boolean | null;
  isObject: (data: unknown) => boolean;
  deepObjectDiffMapper: (firstObject: AnyObject, secondObject: AnyObject, diffStart?: DataDiff) => DataDiff | null;
  formatDataForClipboard: (data: AnyObject, fields: Array<{key: string; label: string; permission?: unknown; formatter?: (value: unknown) => unknown}>) => string;
  addInfraClassName: (classnames: string, isInfra?: boolean) => string;
};

const dataHelper: DataHelperObject = {
  getValueTextRow(value, heading, permission) {
    return permission && value ? `${heading}: ${cleanTextElement(value)}\n` : "";
  },
  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  processDate(value) {
    if (!value) {
      return "";
    }
    return moment(new Date(value as string)).format("DD/MM/YYYY HH:mm");
  },
  formatDate(value) {
    if (!value) {
      return "";
    }
    return moment(new Date(value as string)).format("DD/MM/YYYY");
  },
  managedTypes: ["none", "juniper", "arubaMaster", "arubaDrone", "mikrotik"],
  getRandomInt(min, max) {
    if (min === max) {
      return min;
    }
    const safeMin = Math.ceil(min);
    const safeMax = Math.floor(max);
    return Math.floor(Math.random() * (safeMax - safeMin)) + safeMin;
  },
  states: {
    australia: [
      {value: "QLD", text: "Queensland"},
      {value: "NSW", text: "New South Wales"},
      {value: "VIC", text: "Victoria"},
      {value: "ACT", text: "Australian Capital Territory"},
      {value: "NT", text: "Northern Territory"},
      {value: "WA", text: "Western Australia"},
      {value: "TAS", text: "Tasmania"},
    ],
  },
  getOrderType(key) {
    switch (key) {
      case "PROVISIONING":
        return 6;
      case "SCHEDULED":
        return 5;
      case "ACTIVE":
        return 4;
      case "CANCELLING":
        return 3;
      case "CANCELLED":
        return 2;
      case "COMPLETED":
        return 1;
      case "WAITING":
        return 0;
      case "ERROR":
        return -1;
      default:
        return 0;
    }
  },
  renewalTypes: ["UNKNOWN", "COMPLETED", "CANCELLED", "CANCELLING", "ACTIVE", "SCHEDULED"],
  toUtc(value) {
    if (!value) {
      return "";
    }
    return moment.utc(value).format("YYYY-MM-DDTHH:mm:ssZ");
  },
  countries: [
    {text: "Australia", value: "au"},
    {text: "New Zealand", value: "nz"},
  ],
  removeDashOnText(value) {
    return value.replace(/-/g, "");
  },
  isObjEmpty(obj) {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== null && obj[key] !== "") {
        return true;
      }
    }
    return false;
  },
  isArrayEmpty(arr) {
    if (arr === undefined || arr === null) {
      return true;
    }
    if (Array.isArray(arr) && arr.length === 0) {
      return true;
    }
    if (Array.isArray(arr) && arr.every((item) => item === "" || item === null || item === undefined)) {
      return true;
    }
    return false;
  },
  arrayHasEmptyValue(arr) {
    if (arr === undefined || arr === null) {
      return true;
    }
    if (Array.isArray(arr) && arr.length === 0) {
      return true;
    }
    if (Array.isArray(arr) && arr.some((item) => item === "" || item === null || item === undefined)) {
      return true;
    }
    return false;
  },
  hasTimeVariables(sqlQueryString) {
    return sqlQueryString.includes("$__timeGroupAlias") || sqlQueryString.includes("$__timeFilter");
  },
  basicArraysAreEqual(array1, array2) {
    if (array1.length !== array2.length) {
      return false;
    }

    const array2Sorted = [...array2].sort();
    return [...array1].sort().every((val, index) => {
      if (Array.isArray(val)) {
        return this.basicArraysAreEqual(val, array2Sorted[index] as unknown[]);
      }

      if (this.isObject(val)) {
        return this.basicObjectsAreEqual(val as AnyObject, array2Sorted[index] as AnyObject);
      }

      return val === array2Sorted[index];
    });
  },
  basicObjectsAreEqual(obj1, obj2, isEqual = true) {
    if (!this.isObject(obj1) || !this.isObject(obj2)) {
      return null;
    }

    let allEqual = isEqual;

    Object.keys(obj1).forEach((key) => {
      if (!(key in obj2)) {
        allEqual = false;
      }
    });

    Object.keys(obj2).forEach((key) => {
      if (!(key in obj1)) {
        allEqual = false;
      } else if (Array.isArray(obj2[key])) {
        if (!this.basicArraysAreEqual(obj2[key], obj1[key])) {
          allEqual = false;
        }
      } else if (this.isObject(obj2[key])) {
        if (!this.basicObjectsAreEqual(obj2[key] as AnyObject, obj1[key] as AnyObject, allEqual)) {
          allEqual = false;
        }
      } else if (obj1[key] !== obj2[key]) {
        allEqual = false;
      }
    });

    return allEqual;
  },
  isObject(data) {
    return typeof data === "object" && data !== null;
  },
  deepObjectDiffMapper(firstObject, secondObject, diffStart = {}) {
    const diff: DataDiff = {...diffStart};
    if (!this.isObject(firstObject) || !this.isObject(secondObject)) {
      return null;
    }

    Object.keys(secondObject).forEach((key) => {
      if (!(key in firstObject)) {
        diff.added = {
          ...(diff.added ?? {}),
          [key]: secondObject[key],
        };
        return;
      }

      if (Array.isArray(secondObject[key])) {
        const isEqualArray = this.basicArraysAreEqual(firstObject[key] as unknown[], secondObject[key] as unknown[]);
        if (!isEqualArray) {
          diff.updated = [
            ...(diff.updated ?? []),
            {
              key,
              original: firstObject[key],
              updated: secondObject[key],
            },
          ];
          return;
        }
        return;
      }

      if (this.isObject(secondObject[key])) {
        if (!this.basicObjectsAreEqual(firstObject[key] as AnyObject, secondObject[key] as AnyObject)) {
          diff.updated = [
            ...(diff.updated ?? []),
            {
              key,
              original: firstObject[key],
              updated: secondObject[key],
            },
          ];
          return;
        }
        return;
      }

      if (secondObject[key] !== firstObject[key]) {
        diff.updated = [
          ...(diff.updated ?? []),
          {
            key,
            original: firstObject[key],
            updated: secondObject[key],
          },
        ];
      }
    });

    Object.keys(firstObject).forEach((key) => {
      if (!(key in secondObject)) {
        diff.removed = {
          ...(diff.removed ?? {}),
          [key]: firstObject[key],
        };
      }
    });

    return diff;
  },
  formatDataForClipboard(data, fields) {
    if (!data || !fields || !Array.isArray(fields)) {
      return "";
    }

    return fields
      .map(({key, label, permission, formatter}) => {
        const value = data[key];
        const formattedValue = formatter ? formatter(value) : value;

        return this.getValueTextRow(formattedValue, label, permission);
      })
      .filter(Boolean)
      .join("");
  },
  addInfraClassName(classnames, isInfra) {
    return `${classnames} ${isInfra ? "is-infra" : ""}`;
  },
};

export default dataHelper;

